import { useForm } from "react-hook-form";
import {
  useDeleteUserMutation,
  useUpdateUserFormMutation,
  useUpdateUserProfileMutation,
} from "../../../libs/user/api";
import { selectGetUserResult } from "../../../libs/user/selectors";
import {
  PERSONAL_PROFILE_FORM_FIELDS,
  PersonalProfileFormType,
  FormField,
} from "./fields";
import { useState } from "react";
import { DeleteModal } from "../../Modal/DeleteModal";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import { useAppSelector } from "../../../store";
import { InfoItem } from "./UserProfileFormItem";
import React from "react";

// ── Section definitions ──────────────────────────────────────────────────────

type Section = {
  key: string;
  title: string;
  icon: React.ReactNode;
  fields: (keyof PersonalProfileFormType)[];
  columns?: number; // grid columns override (default: 3)
};

const FORM_SECTIONS: Section[] = [
  {
    key: "personal",
    title: "Dati Personali",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    ),
    fields: [
      "name",
      "surname",
      "gender",
      "birthdate",
      "email",
      "phoneNumber",
      "address",
      "fiscalCode",
    ],
  },
  {
    key: "body",
    title: "Misure Fisiche",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
      </svg>
    ),
    fields: ["height", "weight", "leanBodyMass"],
  },
  {
    key: "training",
    title: "Profilo Allenamento",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
      </svg>
    ),
    fields: [
      "activityLevel",
      "weeklyWorkoutDays",
      "workoutDuration",
      "workoutTimeSlot",
      "stepsPerDay",
      "workoutSince",
    ],
  },
  {
    key: "notes",
    title: "Status & Note",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    fields: ["status", "goal", "paymentNotes", "profileNotes"],
  },
];

// ── Build a map: fieldName → FormField from the flat array ───────────────────

const fieldMap = new Map<string, FormField>();
PERSONAL_PROFILE_FORM_FIELDS.forEach((f) => fieldMap.set(f.name, f));

// ── Component ────────────────────────────────────────────────────────────────

export const UserProfileForm: React.FC = () => {
  const navigate = useNavigate();
  const { id: userId } = useParams();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const user = useAppSelector((state) => selectGetUserResult(state, userId));

  const [deleteUser, { isLoading: isLoadingDelete, error: errorDelete }] =
    useDeleteUserMutation();

  const [
    updateUserProfile,
    { error: userProfileUpdateError, isLoading: isUserProfileUpdatePending },
  ] = useUpdateUserProfileMutation();

  const [
    updateUserForm,
    { error: userFormUpdateError, isLoading: isUserFormUpdatePending },
  ] = useUpdateUserFormMutation();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PersonalProfileFormType>();

  const onSave = (data: PersonalProfileFormType) => {
    if (user) {
      updateUserProfile({
        profileInfo: data,
        currentUser: user,
      })
        .unwrap()
        .then(() => {
          updateUserForm({
            userFormData: data,
            currentUser: user,
          })
            .unwrap()
            .then(() => {
              setIsEditMode(false);
            });
        });
    }
  };

  const isSaving = isUserProfileUpdatePending || isUserFormUpdatePending;
  const hasError = errorDelete || userProfileUpdateError || userFormUpdateError;

  return (
    <form onSubmit={handleSubmit(onSave)}>
      {/* ── Action bar ── */}
      <div className="flex items-center justify-between mb-6">
        <div /> {/* spacer */}
        <div className="flex items-center gap-3">
          {isEditMode ? (
            <>
              <button
                type="button"
                onClick={() => setIsEditMode(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 transition-colors disabled:opacity-50"
              >
                {isSaving ? "Salvataggio..." : "Salva modifiche"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setIsEditMode(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
                Modifica
              </button>
              <DeleteModal
                title={`Elimina ${user?.name} ${user?.surname}`}
                onDelete={() => {
                  user && deleteUser(user);
                  navigate(ROUTES.DASHBOARD);
                }}
                isDeleteInProgress={isLoadingDelete}
              />
            </>
          )}
        </div>
      </div>

      {hasError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">
            C&apos;e&apos; stato un errore, riprova piu&apos; tardi!
          </p>
        </div>
      )}

      {/* ── Cards grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {FORM_SECTIONS.map((section) => {
          const sectionFields = section.fields
            .map((name) => fieldMap.get(name))
            .filter(Boolean) as FormField[];

          // Notes section spans full width
          const isFullWidth = section.key === "notes" || section.key === "training";

          // Determine inner grid columns
          const textareaFields = sectionFields.filter((f) => f.type === "textarea");
          const nonTextareaFields = sectionFields.filter((f) => f.type !== "textarea");

          return (
            <div
              key={section.key}
              className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 ${
                isFullWidth ? "lg:col-span-2" : ""
              }`}
            >
              {/* Card header */}
              <div className="flex items-center gap-2.5 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="text-gray-400 dark:text-gray-500">
                  {section.icon}
                </div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                  {section.title}
                </h3>
              </div>

              {/* Card body — grid */}
              <div
                className={`grid gap-x-8 gap-y-5 ${
                  section.key === "body"
                    ? "grid-cols-1 sm:grid-cols-3"
                    : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
                }`}
              >
                {nonTextareaFields.map((field) => (
                  <InfoItem
                    key={field.name}
                    fieldObj={field}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    isEditMode={isEditMode}
                  />
                ))}
              </div>

              {/* Textarea fields in their own row */}
              {textareaFields.length > 0 && (
                <div
                  className={`grid gap-x-8 gap-y-5 mt-5 ${
                    textareaFields.length === 1
                      ? "grid-cols-1"
                      : textareaFields.length === 2
                      ? "grid-cols-1 sm:grid-cols-2"
                      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                  }`}
                >
                  {textareaFields.map((field) => (
                    <InfoItem
                      key={field.name}
                      fieldObj={field}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      isEditMode={isEditMode}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </form>
  );
};
